import React, { useMemo, useState } from 'react';
import { Form, Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';
import type { ReportParameter } from '../../types';

export interface DynamicSelectProps {
  param: ReportParameter;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  form: any;
  reportCode?: string;
  reportCategory?: string;
  providerName: string;
  /** When true the API is asked to prepend an "All <allLabel>" option with value=null */
  includeAll?: boolean;
  allLabel?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
}

/**
 * Generic dynamic select component that fetches options from the select-field-values API.
 * The providerName determines which backend data provider to query.
 *
 * When includeAll is true (default for optional report params), the backend prepends an
 * "All ..." option with value=null. Selecting it (or clearing) submits null to the backend,
 * which means "no filter" — the -1 sentinel is no longer used.
 *
 * Used for parameter types: SELECT (with optionsEndpoint), ACCOUNT_SELECT, CUSTOMER_SELECT,
 * and any other type that resolves to a backend data provider.
 */
const DynamicSelect: React.FC<DynamicSelectProps> = ({
  param,
  disabled,
  placeholder,
  allowClear = true,
  form,
  reportCode,
  reportCategory,
  providerName,
  includeAll,
  allLabel,
  value,
  onChange,
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const dependsOnValue = Form.useWatch(param.dependsOn ? param.dependsOn : undefined, form);

  // For optional report params, default to including an All option
  const shouldIncludeAll = includeAll ?? !param.required;
  const resolvedAllLabel = allLabel ?? `All ${param.label}`;

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      search,
      fieldName: param.name,
      entityType: 'REPORT',
      reportCode,
      reportCategory,
      ...(shouldIncludeAll ? { includeAll: true, allLabel: resolvedAllLabel } : {}),
    };

    if (param.dependsOn) {
      p[param.dependsOn] = dependsOnValue;
    }

    return p;
  }, [search, param.name, param.dependsOn, dependsOnValue, reportCode, reportCategory, shouldIncludeAll, resolvedAllLabel]);

  const {
    data: options,
    isLoading,
    error,
  } = useGetSelectFieldValues(providerName, params as any, {
    query: {
      enabled: !!providerName,
      staleTime: 5 * 60 * 1000,
    },
  });

  const dynamicOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list
      .filter((opt) => opt.value !== '-1' && (opt.value as any) !== -1)
      .map((opt) => ({
        value: opt.value ?? null,
        label: opt.label,
        disabled: opt.disabled,
      }));
  }, [options]);

  // Treat -1 sentinel as unselected
  const normalizedValue = value === '-1' || (value as any) === -1 ? undefined : value;

  // Default placeholder: "All <Label>" for optional fields
  const resolvedPlaceholder = placeholder ?? (shouldIncludeAll ? resolvedAllLabel : undefined);

  return (
    <Select
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      loading={isLoading}
      showSearch
      allowClear={allowClear}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      options={dynamicOptions}
      value={normalizedValue}
      onChange={onChange}
      notFoundContent={
        error
          ? 'Failed to load options'
          : isLoading
            ? 'Loading...'
            : 'No options'
      }
      optionFilterProp="label"
    />
  );
};

export default DynamicSelect;
